import get from 'lodash/get';
import { push } from 'connected-react-router';

import { MENU, MENU_ITEM } from 'menus/constants/entities';
import { getBaseMenuPathForLocation } from 'menus/selectors';
import { CART } from 'cart/constants/entities';
import { MENU_HOURS_EXPIRED } from 'cart/constants/notificationTypes';

import { addToCart, updateCartItem } from 'cart/actions';
import { closeModal, openModal } from 'base/actions/modal';
import { openNotificationModal } from 'base/actions/notificationModals';
import { restartOnlineOrderingFlow } from 'base/actions/setupInitialData';
import { generatePath } from 'utils/generatePath';
import { isMobileMedia } from 'utils/media';
import { getOnlineOrderingUrlParams } from 'utils/url';
import {
  areFulfillmentDeliveryOptionsSetSelector,
  getCartItemByIdSelector
} from 'cart/selectors';
import { goToOrderSettingsViaSlug } from 'locations/actions/locations';
import { getMenuItemEndpoint } from 'base/constants/endpoints';
import { fetchSingleEntity } from 'base/actions/singleEntity';

export const fetchMenuItemEntity = menuItemSlug => (dispatch, getState) => {
  const endpoint = getMenuItemEndpoint(menuItemSlug);
  dispatch(fetchSingleEntity(MENU_ITEM, endpoint)).then(response => {
    if (response.payload.status === 404) {
      if (get(response.payload, 'response.error') === 'No Menu found.') {
        dispatch(openNotificationModal(MENU_HOURS_EXPIRED));
      } else {
        dispatch(menuItemModule.closeMenuItemAndGoToMenu());
      }
    }
  });
};

const buildMods = modifications =>
  Object.keys(modifications).reduce((acc, curr) => {
    const currentItem = modifications[curr];

    if (['string', 'number'].includes(typeof currentItem)) {
      acc[curr] = [currentItem];
    } else {
      acc[curr] = Object.keys(currentItem).filter(item => currentItem[item]);
    }

    return acc;
  }, {});

const buildMenuItemFormPayload = (values, productId) => {
  const {
    quantityInput,
    'order-made-for': orderMadeFor,
    specialInstructions,
    ...modifications
  } = values;

  const payload = {
    product_id: productId,
    quantity: quantityInput,
    order_made_for: orderMadeFor,
    special_instructions: specialInstructions,
    required_fields: {
      ...buildMods(modifications)
    }
  };

  return payload;
};

export const handleFormItemSubmit = (values, props) => dispatch => {
  dispatch(handleFormSubmit(values, props));
};

export const handleFormItemUpdate = (values, props) => dispatch => {
  const cartItemId = props.match.params.cartItemId;
  dispatch(handleFormUpdate(cartItemId, values));
};

/**
 * Action to update the an existing cart item
 */
export const handleFormUpdate = (cartItemId, values) => async (
  dispatch,
  getState
) => {
  const { cart } = getState();
  const id = parseInt(cartItemId);
  const cartItemObject = getCartItemByIdSelector(cart, id);
  const productId = cartItemObject.product;
  const payload = menuItemModule.buildMenuItemFormPayload(values, productId);
  const result = await dispatch(updateCartItem(id, payload));

  if (!get(result, ['payload', 'response', 'error'], '')) {
    dispatch(menuItemModule.returnToCart());
  }
};

/**
 * Action to add new menu item to cart
 */
export const handleFormSubmit = (values, props) => async dispatch => {
  const id = props.productId;

  const payload = menuItemModule.buildMenuItemFormPayload(values, id);
  const result = await dispatch(addToCart(payload));
  if (result.payload.status === 404) {
    dispatch(restartOnlineOrderingFlow());
  } else {
    dispatch(goToNextStepAfterAdd());
  }
};

const goToNextStepAfterAdd = () => (dispatch, getState) => {
  const { cart } = getState();
  const areFulfillmentOptionsSet = areFulfillmentDeliveryOptionsSetSelector(
    cart
  );
  if (areFulfillmentOptionsSet) {
    return dispatch(menuItemModule.closeMenuItemAndGoToMenu());
  }

  dispatch(closeMenuItemAndGoToOrderSettings());
};
const closeMenuItemAndGoToOrderSettings = () => (dispatch, getState) => {
  const { router } = getState();
  const { locationSlug } = getOnlineOrderingUrlParams(router.location.pathname);
  dispatch(goToOrderSettingsViaSlug(locationSlug));
};

export const returnToCart = () => dispatch => {
  dispatch(push(generatePath(CART, 'cart')));
};

export const closeMenuItemAndGoToMenu = () => (dispatch, getState) => {
  dispatch(closeModal());
  const path = getBaseMenuPathForLocation(getState());
  dispatch(push(path));
};

export const navigateToMenuItem = slug => (dispatch, getState) => {
  const state = getState();

  if (!isMobileMedia()) {
    dispatch(openModal());
  }

  const { locationSlug } = getOnlineOrderingUrlParams(
    state.router.location.pathname
  );

  dispatch(
    push(
      generatePath(MENU, 'menuItemDetail', {
        slug: locationSlug,
        menuItemSlug: slug
      })
    )
  );
};

// package this module for export.
// any method defined here that uses one of these methods
// should use the version attached to menuItemModule
//  this makes it simple to mock certain pieces of the module
// for tests
const menuItemModule = {
  handleFormItemUpdate,
  handleFormUpdate,
  updateCartItem,
  returnToCart,
  buildMenuItemFormPayload,
  fetchMenuItemEntity,
  closeMenuItemAndGoToMenu
};

export default menuItemModule;
